import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ActualFormModal } from "../components/cashflow/ActualFormModal";
import { PositionForm } from "../components/cashflow/PositionForm";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Modal } from "../components/ui/Modal";
import { useAccounts } from "../hooks/accounts";
import {
  useAddActual,
  useCashflowPosition,
  useDeleteActual,
  useHardDeletePosition,
  useSoftDeletePosition,
  useUpdatePosition,
} from "../hooks/cashflow";
import type { ActualDto, CreateActualInput, UpdatePositionInput } from "../lib/cashflow";
import { mapCashflowError } from "../lib/cashflow-errors";

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatMonth(ym: string): string {
  const parts = ym.split("-").map(Number);
  const y = parts[0] ?? 2000;
  const m = parts[1] ?? 1;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("de-DE", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function CashflowDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const position = useCashflowPosition(id ?? "");
  const accounts = useAccounts({ active: "true" });
  const update = useUpdatePosition();
  const softDelete = useSoftDeletePosition();
  const hardDelete = useHardDeletePosition();
  const addActual = useAddActual();
  const deleteActual = useDeleteActual();
  const [editOpen, setEditOpen] = useState(false);
  const [actualOpen, setActualOpen] = useState(false);
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);
  const [editError, setEditError] = useState<string | undefined>();
  const [conflictMonths, setConflictMonths] = useState<string[] | null>(null);

  if (position.isLoading || !position.data) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const p = position.data;

  async function handleEditSubmit(input: UpdatePositionInput) {
    setEditError(undefined);
    setConflictMonths(null);
    try {
      await update.mutateAsync({ id: p.id, input });
      setEditOpen(false);
    } catch (err) {
      const code =
        err instanceof Error && "code" in err ? (err as { code: string }).code : undefined;
      if (code === "ACTUALS_CONFLICT" && err instanceof Error && "details" in err) {
        const details = (err as { details?: { offendingActualMonths?: string[] } }).details;
        setConflictMonths(details?.offendingActualMonths ?? []);
      }
      setEditError(code ? (t(mapCashflowError(code)) ?? t("errors.unknown")) : t("errors.unknown"));
    }
  }

  async function handleAddActual(input: CreateActualInput) {
    await addActual.mutateAsync({ positionId: p.id, input });
  }

  async function handleReplaceActual(existing: ActualDto, input: CreateActualInput) {
    await deleteActual.mutateAsync({ positionId: p.id, actualId: existing.id });
    await addActual.mutateAsync({ positionId: p.id, input });
  }

  async function handleSoftDelete() {
    await softDelete.mutateAsync(p.id);
    // Nach Soft-Delete zurueck zur Liste — sonst bleibt die Detail-Seite offen
    // und der User denkt das Loeschen hat nicht funktioniert.
    nav("/cashflow");
  }

  async function handleReactivate() {
    await update.mutateAsync({ id: p.id, input: { isActive: true } });
  }

  async function handleHardDelete() {
    await hardDelete.mutateAsync(p.id);
    setConfirmHardDelete(false);
    nav("/cashflow");
  }

  const hardDeleteDisabled = p.actuals.length > 0;

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--muted)" }}>
            <Link to="/cashflow" className="hover:underline">
              {t("nav.cashflow")}
            </Link>
            <span>/</span>
            <span style={{ color: "var(--text)" }}>{p.name}</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{p.name}</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            onClick={() => setEditOpen(true)}
          >
            {t("cashflow.detail.edit")}
          </button>
          {p.isActive ? (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={handleSoftDelete}
            >
              {t("cashflow.detail.deactivate")}
            </button>
          ) : (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={handleReactivate}
            >
              {t("cashflow.detail.activate")}
            </button>
          )}
        </div>
      </div>

      <section className="fv-card p-5 grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">{t("cashflow.detail.field.kind")}: </span>
          {t(`cashflow.kind.${p.kind}`)}
        </div>
        <div>
          <span className="text-gray-500">{t("cashflow.detail.field.amount")}: </span>
          {formatEur(p.amountCents)}
        </div>
        <div>
          <span className="text-gray-500">{t("cashflow.detail.field.rhythm")}: </span>
          {t(`cashflow.rhythm.${p.rhythm}`)}
        </div>
        <div>
          <span className="text-gray-500">{t("cashflow.detail.field.sector")}: </span>
          {t(`accounts.sector.${p.sector}`)}
        </div>
        <div>
          <span className="text-gray-500">{t("cashflow.detail.field.firstDue")}: </span>
          {formatMonth(p.firstDueMonth)}
        </div>
        <div>
          <span className="text-gray-500">{t("cashflow.detail.field.endMonth")}: </span>
          {p.endMonth ? formatMonth(p.endMonth) : "—"}
        </div>
        <div>
          <span className="text-gray-500">{t("cashflow.detail.field.account")}: </span>
          {p.account ? (
            <Link to={`/accounts/${p.account.id}`} className="text-blue-600">
              {p.account.name}
            </Link>
          ) : (
            "—"
          )}
          {p.account && !p.account.isActive && (
            <span className="ml-2 text-xs bg-gray-200 px-1 rounded">
              {t("cashflow.detail.field.account_inactive")}
            </span>
          )}
        </div>
        <div>
          <span className="text-gray-500">{t("cashflow.detail.field.category")}: </span>
          {p.category ?? "—"}
        </div>
      </section>

      <section>
        <header className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">{t("cashflow.detail.history")}</h2>
          <button
            type="button"
            className="px-3 py-1 bg-blue-600 text-white rounded"
            onClick={() => setActualOpen(true)}
          >
            {t("cashflow.detail.addActual")}
          </button>
        </header>
        {p.actuals.length === 0 ? (
          <p className="text-gray-500">{t("cashflow.detail.history_empty")}</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b">
                <th className="p-2">{t("cashflow.detail.column.month")}</th>
                <th className="p-2 text-right">{t("cashflow.detail.column.actual")}</th>
                <th className="p-2">{t("cashflow.detail.column.note")}</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {p.actuals.map((a) => (
                <tr key={a.id} id={`actual-${a.month}`} className="border-b">
                  <td className="p-2">{formatMonth(a.month)}</td>
                  <td className="p-2 text-right font-mono">{formatEur(a.actualCents)}</td>
                  <td className="p-2">{a.note ?? "—"}</td>
                  <td className="p-2">
                    <button
                      type="button"
                      className="text-red-700 text-sm"
                      onClick={() => deleteActual.mutate({ positionId: p.id, actualId: a.id })}
                    >
                      {t("cashflow.detail.deleteActual")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="border-t pt-4">
        <button
          type="button"
          className="text-red-700 underline disabled:opacity-50"
          onClick={() => setConfirmHardDelete(true)}
          disabled={hardDeleteDisabled}
          title={
            hardDeleteDisabled ? (t("cashflow.detail.hardDelete.disabledHint") ?? "") : undefined
          }
        >
          {t("cashflow.detail.hardDelete.label")}
        </button>
      </section>

      {editOpen && (
        <Modal open={editOpen} onClose={() => setEditOpen(false)} title={t("cashflow.detail.edit")}>
          <PositionForm
            mode="edit"
            initial={p}
            accounts={accounts.data?.accounts ?? []}
            onSubmit={handleEditSubmit}
            submitting={update.isPending}
            serverError={editError}
          />
          {conflictMonths && (
            <div className="mt-3 border border-red-300 bg-red-50 rounded p-2 text-sm">
              <p>{t("cashflow.detail.conflict_explanation")}</p>
              <ul className="list-disc ml-5 mt-1">
                {conflictMonths.map((m) => (
                  <li key={m}>
                    <a
                      href={`#actual-${m}`}
                      className="text-blue-700 underline"
                      onClick={() => setEditOpen(false)}
                    >
                      {formatMonth(m)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal>
      )}

      {actualOpen && (
        <ActualFormModal
          position={p}
          open={actualOpen}
          onClose={() => setActualOpen(false)}
          onSubmit={handleAddActual}
          onReplace={handleReplaceActual}
        />
      )}

      {confirmHardDelete && (
        <ConfirmDialog
          open={confirmHardDelete}
          title={t("cashflow.detail.hardDelete.title")}
          message={t("cashflow.detail.hardDelete.message", { name: p.name })}
          confirmLabel={t("cashflow.detail.hardDelete.confirm")}
          destructive
          onConfirm={handleHardDelete}
          onCancel={() => setConfirmHardDelete(false)}
        />
      )}
    </section>
  );
}
